import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { AlertTriangle, Download, RefreshCw, TrendingDown, TrendingUp } from 'lucide-react';
import {
  AdminHead,
  Avatar,
  Badge,
  Banner,
  Button,
  EmptyRow,
  FilterMenu,
  SearchField,
  Segmented,
  SkeletonRows,
  StatusPill,
  relativeTime,
} from '../../kit';
import { useIsMobile } from '../../../../hooks/use-mobile';
import { downloadUserExport } from '../../userExport';
import { ROLES, ROLE_LABELS } from '../userModel';
import { apiErrorMessage, bulkUsers, getUsage, isAbortError, resendInvite } from '../usersApi';
import { BreakdownCards, HeatmapCard, TrendCard } from './UsageCharts';
import UsageDrill from './UsageDrill';
import { pageTitle } from './usagePages';
import {
  CHANNEL_LABELS,
  DEFAULT_FILTERS,
  DEVICE_OPTIONS,
  PERIODS,
  REPORT_LABELS,
  SEGMENTS,
  activeNow,
  STATUS_META,
  USAGE_OPTIONS,
  dateAt,
  exportRows,
  formatDay,
  mixLabel,
  peopleRows,
  summarize,
  tagOptions,
} from './usageModel';
import './usage.css';

const PAGE_SIZE = 25;
const LIVE_POLL_MS = 60 * 1000;
const fmt = (n) => Math.round(n || 0).toLocaleString('en-US');

function periodLabel(report) {
  const p = report?.period;
  if (!p?.from) return '';
  const from = dateAt(p.from, 0);
  const to = dateAt(p.from, p.days - 1);
  if (p.days === 1) return `Today, ${formatDay(to)}`;
  return `${formatDay(from)} – ${formatDay(to)}, ${to.getUTCFullYear()}`;
}

function lastActiveSub(user) {
  if (!user.lastSeenAt) {
    if (user.status === 'invited' && user.invitedAt) return `Invited ${relativeTime(user.invitedAt)}`;
    if (user.lastSignInAt) return `Signed in ${relativeTime(user.lastSignInAt)}`;
    return null;
  }
  const via = CHANNEL_LABELS[user.lastSeenChannel] || '';
  if (user.lastSeenChannel === 'email') return `${REPORT_LABELS[user.lastSeenWhere] || 'Report'} email`;
  const where = pageTitle(user.lastSeenWhere);
  return [via, where].filter(Boolean).join(' · ');
}

function Tile({ label, value, sub, tone, onClick, children }) {
  return (
    <button type="button" className={`ak-stat${tone ? ` uu-${tone}` : ''}`} onClick={onClick}>
      <span className="ak-stat-label">{label}</span>
      <span className="ak-stat-value">{value}{children}</span>
      {sub ? <span className="ak-stat-sub">{sub}</span> : null}
    </button>
  );
}

function Delta({ now, before, show }) {
  if (!show) return null;
  const d = now - before;
  if (!d) return <span className="ak-stat-sub">Same as the prior period</span>;
  const Icon = d > 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`ak-stat-sub uu-delta${d > 0 ? ' uu-up' : ' uu-down'}`}>
      <Icon aria-hidden="true" />
      {d > 0 ? '+' : '−'}{Math.abs(d)} vs prior period
    </span>
  );
}

function SourceBanner({ source }) {
  if (!source?.error) {
    return source?.truncated ? (
      <Banner tone="warn" icon={AlertTriangle} title="Showing partial activity">
        This period has more activity than one query returns. Pick a shorter period for exact numbers.
      </Banner>
    ) : null;
  }
  if (source.error === 'not_configured') {
    return (
      <Banner tone="warn" icon={AlertTriangle} title="Activity data isn't connected">
        PostHog isn't set up on the server, so only sign-in status is shown. Everyone without a sign-in counts as not using Lodgeic.
      </Banner>
    );
  }
  return (
    <Banner tone="bad" icon={AlertTriangle} title="Couldn't load activity">
      {source.error}. People and sign-in status are still shown.
    </Banner>
  );
}

/**
 * Phones: one tappable card per person — who, status, last active, the app-day
 * strip — with Resend invite on its own 44px button.
 */
function PeopleCards({ rows, loading, emptyBody, busy, live, onOpenUser, onResend }) {
  if (loading) {
    return (
      <ul className="uu-m-people" aria-label="People" aria-busy="true">
        {Array.from({ length: 5 }).map((_, i) => (
          <li key={i} className="uu-m-person"><div className="ak-sk" style={{ width: '60%' }} /><div className="ak-sk" style={{ width: '40%' }} /></li>
        ))}
      </ul>
    );
  }
  if (!rows.length) {
    return <div className="ak-empty"><strong>No one here</strong>{emptyBody}</div>;
  }
  return (
    <ul className="uu-m-people" aria-label="People">
      {rows.map((r) => {
        const u = r.user;
        const meta = STATUS_META[r.status];
        const canResend = r.status === 'never' && u.status === 'invited';
        const sub = lastActiveSub(u);
        return (
          <li key={u.id} className="uu-m-person">
            <button type="button" className="uu-m-person-main" onClick={() => onOpenUser && onOpenUser(u.id)}>
              <Avatar name={u.name} email={u.email} />
              <span className="uu-m-person-text">
                <span className="uu-m-person-top">
                  <span className="uu-m-person-name">{u.name}</span>
                  <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                </span>
                <span className="ak-cell-sub">
                  {u.lastSeenAt ? relativeTime(u.lastSeenAt) : 'Never active'}{sub ? ` · ${sub}` : ''}
                </span>
                <span className="uu-strip-cell" aria-label={`${r.activeDays} ${live ? 'app today' : 'app days'}${r.opens ? `, ${r.opens} email opens` : ''}`}>
                  <span className="uu-strip" aria-hidden="true">
                    {r.strip.map((k, i) => <span key={i} className={`uu-day uu-${k}`} />)}
                  </span>
                  <span className="uu-strip-n" aria-hidden="true">{r.activeDays}</span>
                  {r.opens ? <span className="ak-cell-sub" aria-hidden="true">{fmt(r.opens)} opens</span> : null}
                </span>
              </span>
            </button>
            {canResend ? (
              <button type="button" className="ak-btn uu-m-resend" disabled={busy} onClick={() => onResend(u)}>Resend invite</button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Admin → Users → Usage. Loads one report per org + period; every other
 * control filters it client-side (usageModel.js).
 */
export default function UsageView({ tabs, activeTab, onTabChange, onOpenUser }) {
  const [org, setOrg] = useState(null);
  const [days, setDays] = useState(30);
  const [reload, setReload] = useState(0);
  const [state, setState] = useState({ loading: true, error: null, report: null });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [segment, setSegment] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [drill, setDrill] = useState(null);
  const isMobile = useIsMobile();
  // Today is live: re-fetch every minute while the tab is visible, quietly
  // (the server caches today for 30s, so this stays cheap).
  const live = days === 1;
  const [tick, setTick] = useState(0);
  const polling = useRef(false);
  useEffect(() => {
    if (!live) return undefined;
    const poll = () => {
      if (document.visibilityState !== 'visible') return;
      polling.current = true;
      setTick((n) => n + 1);
    };
    const id = setInterval(poll, LIVE_POLL_MS);
    document.addEventListener('visibilitychange', poll);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', poll); };
  }, [live]);

  useEffect(() => {
    const ctrl = new AbortController();
    const quiet = polling.current;
    polling.current = false;
    if (!quiet) setState((s) => ({ ...s, loading: true, error: null }));
    getUsage({ org, days, refresh: reload > 0 }, { signal: ctrl.signal })
      .then((report) => {
        setState({ loading: false, error: null, report });
        if (!org && report?.org?.code) setOrg(report.org.code);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        // a failed background refresh keeps the last numbers on screen
        if (quiet) return;
        setState({ loading: false, error: apiErrorMessage(err, "Couldn't load the usage report."), report: null });
      });
    return () => ctrl.abort();
  }, [org, days, reload, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const report = state.report;
  const setFilter = useCallback((patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); }, []);
  const pickSegment = (s) => { setSegment(s); setPage(1); };

  const summary = useMemo(() => (report ? summarize(report, filters) : null), [report, filters]);
  const rows = useMemo(
    () => (report && summary ? peopleRows(report, summary, { segment, search }) : []),
    [report, summary, segment, search]
  );
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const shown = rows.slice((Math.min(page, pages) - 1) * PAGE_SIZE, Math.min(page, pages) * PAGE_SIZE);

  const invitedNever = useMemo(() => {
    if (!report || !summary) return [];
    return summary.scope.filter((i) => summary.facts[i].status === 'never' && report.users[i].status === 'invited').map((i) => report.users[i]);
  }, [report, summary]);

  const tags = useMemo(() => tagOptions(report?.users), [report]);
  const orgOptions = (report?.orgs || []).map((o) => ({ value: o.code, label: o.name }));
  if (orgOptions.length > 1) orgOptions.unshift({ value: 'all', label: 'All organizations' });
  const nowCount = report && live ? activeNow(report, filters) : 0;
  const updatedAt = report?.generatedAt
    ? new Date(report.generatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
    : null;
  const locationOptions = (report?.locations || []).map((l) => ({ value: l.id, label: l.name }));

  const resendOne = async (user) => {
    setBusy(true);
    try {
      const res = await resendInvite(user.id);
      if (res?.ok === false) toast.error(res.message || "Couldn't resend the invite");
      else toast.success(`Invite re-sent to ${user.email}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't resend the invite"));
    } finally {
      setBusy(false);
    }
  };

  const resendAll = async () => {
    if (!invitedNever.length) return;
    setBusy(true);
    try {
      const res = await bulkUsers('resendInvite', invitedNever.map((u) => u.id));
      const sent = res?.succeeded ?? 0;
      const skipped = res?.skipped ?? 0;
      const failed = res?.failed ?? 0;
      toast[failed ? 'error' : 'success'](
        `${sent} invite${sent === 1 ? '' : 's'} re-sent${skipped ? `, ${skipped} skipped` : ''}${failed ? `, ${failed} failed` : ''}`
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't resend the invites"));
    } finally {
      setBusy(false);
    }
  };

  const exportPeople = async () => {
    if (!report) return;
    try {
      const date = new Date().toISOString().slice(0, 10);
      await downloadUserExport(exportRows(rows, report), `usage-${report.org?.code || 'org'}-${report.period.days}d-${date}.xlsx`);
    } catch (err) {
      toast.error("Couldn't export the report");
    }
  };

  const filtersMeta = (
    <span className="uu-filters-meta" aria-live="polite">
      {state.loading ? (
        <span className="uu-loading"><span className="uu-spinner" aria-hidden="true" />Loading activity…</span>
      ) : report && live ? (
        <span className="uu-live"><span className="uu-live-dot" aria-hidden="true" />Live · {periodLabel(report)} · updated {updatedAt}</span>
      ) : report ? `${periodLabel(report)} · vs the prior ${report.period.days} days` : null}
    </span>
  );
  const periodControl = (
    <Segmented label="Period" options={PERIODS} value={days} onChange={(d) => { setDays(d); setPage(1); }} />
  );
  const head = (
    <AdminHead
      title="Users"
      subtitle="Who's using Lodgeic, when, and how, across the app and emailed reports."
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={onTabChange}
      actions={(
        <>
          {isMobile ? null : periodControl}
          <Button icon={RefreshCw} variant="ghost" className={state.loading ? 'uu-spinning' : undefined} aria-label={state.loading ? 'Loading' : 'Refresh'} title="Refresh" onClick={() => setReload((n) => n + 1)} disabled={state.loading} />
          {isMobile ? (
            <Button icon={Download} onClick={exportPeople} disabled={!report || !rows.length} aria-label="Export" title="Export" />
          ) : (
            <Button icon={Download} onClick={exportPeople} disabled={!report || !rows.length}>Export</Button>
          )}
        </>
      )}
    />
  );

  if (state.error) {
    return (
      <>
        {head}
        <Banner tone="bad" icon={AlertTriangle} title="Couldn't load the usage report" action={<Button onClick={() => setReload((n) => n + 1)}>Try again</Button>}>
          {state.error}
        </Banner>
      </>
    );
  }

  const loading = state.loading && !report;
  // a refetch (period / org / refresh) keeps the old numbers on screen, dimmed
  const stale = state.loading && Boolean(report);
  const c = summary?.counts || { all: 0, app: 0, email: 0, dormant: 0, never: 0, inactive: 0 };
  const segCounts = { all: c.all, app: c.app, email: c.email, inactive: c.inactive };

  return (
    <>
      {head}

      {state.loading ? <div className="uu-loadbar" role="progressbar" aria-label="Loading usage data" /> : null}

      {isMobile ? (
        <div className="uu-m-scope">
          {periodControl}
          {orgOptions.length > 1 ? (
            <FilterMenu label="Organization" single searchable options={orgOptions} value={org ? [org] : []}
              onChange={(v) => { if (v[0]) { setOrg(v[0]); setFilters(DEFAULT_FILTERS); setPage(1); } }} />
          ) : report?.org ? <span className="ak-chip ak-active uu-static-chip">{report.org.name}</span> : null}
        </div>
      ) : null}

      <div className={`uu-filters${isMobile ? ' uu-m-filters' : ''}`}>
        {isMobile ? null : orgOptions.length > 1 ? (
          <FilterMenu label="Organization" single searchable options={orgOptions} value={org ? [org] : []}
            onChange={(v) => { if (v[0]) { setOrg(v[0]); setFilters(DEFAULT_FILTERS); setPage(1); } }} />
        ) : report?.org ? <span className="ak-chip ak-active uu-static-chip">{report.org.name}</span> : null}
        {locationOptions.length > 1 ? (
          <FilterMenu label="Location" searchable options={locationOptions} value={filters.locations} onChange={(locations) => setFilter({ locations })} />
        ) : null}
        <FilterMenu label="Role" options={ROLES.map((r) => ({ value: r.value, label: r.label }))} value={filters.roles} onChange={(roles) => setFilter({ roles })} />
        {tags.length ? <FilterMenu label="Tag" searchable options={tags} value={filters.tags} onChange={(t) => setFilter({ tags: t })} /> : null}
        <span className="uu-sep" aria-hidden="true" />
        <Segmented label="Usage type" mode="choice" options={USAGE_OPTIONS} value={filters.usage} onChange={(usage) => setFilter({ usage })} />
        <Segmented label="Device" mode="choice" options={DEVICE_OPTIONS} value={filters.device} onChange={(device) => setFilter({ device })} />
        {isMobile ? null : filtersMeta}
      </div>
      {isMobile ? filtersMeta : null}

      {report ? <SourceBanner source={report.source} /> : null}

      {loading || !summary ? (
        <div className="uu-skeleton" aria-hidden="true">
          <div className="uu-tiles">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="ak-stat"><div className="ak-sk" style={{ width: '55%' }} /><div className="ak-sk" style={{ width: '35%', height: 22 }} /><div className="ak-sk" style={{ width: '70%' }} /></div>
            ))}
          </div>
          <div className="ak-card uu-card">
            <div className="ak-sk" style={{ width: 160 }} />
            <div className="uu-sk-bars">
              {Array.from({ length: 30 }).map((_, i) => <div key={i} className="ak-sk" style={{ height: `${30 + ((i * 37) % 60)}%` }} />)}
            </div>
          </div>
          <div className="ak-card uu-card">
            <div className="ak-sk" style={{ width: 200 }} />
            <div className="uu-sk-heat">
              {Array.from({ length: 7 * 24 }).map((_, i) => <div key={i} className="ak-sk" />)}
            </div>
          </div>
          <div className="uu-grid3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="ak-card uu-card">
                <div className="ak-sk" style={{ width: 100 }} />
                {Array.from({ length: 4 }).map((__, j) => <div key={j} className="ak-sk" style={{ width: `${90 - j * 12}%` }} />)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={`uu-report${stale ? ' uu-stale' : ''}`} aria-busy={stale}>
          <div className="uu-tiles">
            <Tile label={live ? 'Active today' : 'Active people'} value={fmt(summary.active)} onClick={() => pickSegment('all')}
              sub={live
                ? <span className="ak-stat-sub">{fmt(nowCount)} in the last 15 min</span>
                : <Delta now={summary.active} before={summary.prevActive} show={summary.showDelta && report.source?.posthog && !report.source?.error} />}>
              <span className="uu-of"> of {fmt(c.all)}</span>
            </Tile>
            <Tile label="Used the app" value={fmt(c.app)} onClick={() => pickSegment('app')}
              sub={`Avg ${Math.round(summary.avgDailyApp * 10) / 10} a day`} />
            <Tile label="Email only" value={fmt(c.email)} onClick={() => pickSegment('email')} sub="Read reports, never opened the app" />
            <Tile label="Not using Lodgeic" value={fmt(c.inactive)} tone={c.inactive ? 'warn' : null} onClick={() => pickSegment('inactive')}
              sub={`${fmt(c.dormant)} stopped · ${fmt(c.never)} never signed in`} />
            <Tile label="Report emails opened" value={fmt(summary.opens)} onClick={() => pickSegment('all')}
              sub={`${fmt(summary.readers)} ${summary.readers === 1 ? 'reader' : 'readers'} · ${summary.reportTypes} report ${summary.reportTypes === 1 ? 'type' : 'types'}`} />
          </div>

          <TrendCard report={report} filters={filters} summary={summary} onDrill={setDrill} />
          <HeatmapCard report={report} filters={filters} onDrill={setDrill} />
          <BreakdownCards summary={summary} onDrill={setDrill} />
        </div>
      )}

      <section className="uu-people" aria-labelledby="uu-people-h">
        <div className="uu-section-head">
          <h2 id="uu-people-h">People</h2>
          <span className="ak-help">Who's using it, and who isn't</span>
        </div>

        {summary && c.inactive ? (
          <Banner
            tone="warn"
            icon={AlertTriangle}
            title={`${fmt(c.inactive)} ${c.inactive === 1 ? "person hasn't" : "people haven't"} used Lodgeic in the last ${report.period.days} days`}
            action={(
              <div className="uu-banner-actions">
                <Button onClick={() => pickSegment('inactive')}>Show them</Button>
                {invitedNever.length ? (
                  <Button variant="primary" onClick={resendAll} disabled={busy}>
                    Resend {invitedNever.length} invite{invitedNever.length === 1 ? '' : 's'}
                  </Button>
                ) : null}
              </div>
            )}
          >
            {[
              c.never ? `${fmt(c.never)} never signed in.` : null,
              c.dormant ? `${fmt(c.dormant)} used to and stopped.` : null,
              c.email ? `Another ${fmt(c.email)} only read the emailed reports.` : null,
            ].filter(Boolean).join(' ')}
          </Banner>
        ) : null}

        <div className={`ak-frame${stale ? ' uu-stale' : ''}`} aria-busy={state.loading}>
          <div className={`ak-toolbar${isMobile ? ' uu-m-toolbar' : ''}`}>
            <SearchField value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder={isMobile ? 'Search people' : 'Search name, email, title or tag'} label="Search people" />
            <Segmented
              label="Usage status"
              options={SEGMENTS.map((s) => ({ ...s, count: summary ? segCounts[s.value] : undefined }))}
              value={segment}
              onChange={pickSegment}
            />
            <span className="ak-toolbar-meta" aria-live="polite">{summary ? `${fmt(rows.length)} ${rows.length === 1 ? 'person' : 'people'}` : 'Loading…'}</span>
          </div>
          {isMobile ? (
            <PeopleCards
              rows={shown}
              loading={loading}
              emptyBody={search ? 'Try a different search.' : 'No one in scope matches this status.'}
              busy={busy}
              live={live}
              onOpenUser={onOpenUser}
              onResend={resendOne}
            />
          ) : (
          <div className="ak-scroll">
            <table className="ak-tbl uu-tbl">
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Role</th>
                  <th scope="col">Tags</th>
                  <th scope="col" aria-sort="descending">Last active</th>
                  <th scope="col">{live ? 'App today' : `App days · last ${Math.min(30, report?.period?.days || 30)}d`}</th>
                  <th scope="col">Device mix</th>
                  <th scope="col" className="ak-right">Email opens</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="uu-w-act"><span className="ak-sr">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loading ? <SkeletonRows rows={8} columns={9} /> : null}
                {!loading && !shown.length ? (
                  <EmptyRow colSpan={9} title="No one here" body={search ? 'Try a different search.' : 'No one in scope matches this status.'} />
                ) : null}
                {!loading ? shown.map((r) => {
                  const u = r.user;
                  const meta = STATUS_META[r.status];
                  const canResend = r.status === 'never' && u.status === 'invited';
                  return (
                    <tr key={u.id} className="ak-click" onClick={() => onOpenUser && onOpenUser(u.id)}>
                      <td>
                        <div className="ak-cell-id">
                          <Avatar name={u.name} email={u.email} />
                          <div>
                            <button type="button" className="us-rowlink" onClick={(e) => { e.stopPropagation(); if (onOpenUser) onOpenUser(u.id); }}>{u.name}</button>
                            <span className="ak-cell-sub">{u.jobTitle || u.email}</span>
                          </div>
                        </div>
                      </td>
                      <td>{u.role ? <span className="ak-role">{ROLE_LABELS[u.role] || u.role}</span> : <span className="ak-muted">—</span>}</td>
                      <td>
                        {u.tags?.length ? (
                          <div className="uu-tags">
                            {u.tags.slice(0, 2).map((t) => <Badge key={t}>{t}</Badge>)}
                            {u.tags.length > 2 ? <span className="ak-muted uu-more" title={u.tags.slice(2).join(', ')}>+{u.tags.length - 2}</span> : null}
                          </div>
                        ) : <span className="ak-muted">—</span>}
                      </td>
                      <td>
                        <div className="uu-last">
                          <span>{u.lastSeenAt ? relativeTime(u.lastSeenAt) : 'Never'}</span>
                          {lastActiveSub(u) ? <span className="ak-cell-sub">{lastActiveSub(u)}</span> : null}
                        </div>
                      </td>
                      <td>
                        <div className="uu-strip-cell">
                          <div className="uu-strip" aria-hidden="true">
                            {r.strip.map((k, i) => <span key={i} className={`uu-day uu-${k}`} />)}
                          </div>
                          <span className="uu-strip-n">{r.activeDays}</span>
                        </div>
                      </td>
                      <td>
                        {r.mix ? (
                          <div className="uu-mix-cell" title={`Desktop ${r.mix.desktop}% · Mobile ${r.mix.mobile}% · Email ${r.mix.email}%`}>
                            <div className="uu-mix" aria-hidden="true">
                              <span className="uu-mix-d" style={{ width: `${r.mix.desktop}%` }} />
                              <span className="uu-mix-m" style={{ width: `${r.mix.mobile}%` }} />
                              <span className="uu-mix-e" style={{ width: `${r.mix.email}%` }} />
                            </div>
                            <span className="ak-cell-sub">{mixLabel(r.mix)}</span>
                          </div>
                        ) : <span className="ak-muted">—</span>}
                      </td>
                      <td className="ak-right">{r.opens ? fmt(r.opens) : <span className="ak-muted">—</span>}</td>
                      <td><StatusPill tone={meta.tone}>{meta.label}</StatusPill></td>
                      <td className="uu-w-act">
                        {canResend ? (
                          <Button size="small" disabled={busy} onClick={(e) => { e.stopPropagation(); resendOne(u); }}>Resend invite</Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                }) : null}
              </tbody>
            </table>
          </div>
          )}
          {rows.length > PAGE_SIZE ? (
            <div className={`ak-pager${isMobile ? ' uu-m-pager' : ''}`}>
              {isMobile ? null : <span>{PAGE_SIZE} per page</span>}
              <span className="ak-foot-spacer" />
              <span>Page {Math.min(page, pages)} of {pages}</span>
              <Button size="small" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Previous</Button>
              <Button size="small" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>Next</Button>
            </div>
          ) : null}
        </div>
        <div className="uu-key" aria-hidden="true">
          <span><span className="uu-day uu-app" />Used the app</span>
          <span><span className="uu-day uu-email" />Opened a report email only</span>
          <span><span className="uu-day uu-none" />No activity</span>
          <span className="uu-key-gap">Device mix:</span>
          <span><span className="uu-swatch uu-mix-d" />Desktop</span>
          <span><span className="uu-swatch uu-mix-m" />Mobile</span>
          <span><span className="uu-swatch uu-mix-e" />Email</span>
        </div>
      </section>
      {drill && report ? (
        <UsageDrill
          report={report}
          filters={filters}
          selection={drill}
          onClose={() => setDrill(null)}
          onOpenUser={(id) => { setDrill(null); if (onOpenUser) onOpenUser(id); }}
        />
      ) : null}
    </>
  );
}
